import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Settings2, Plus, Minus, History, Trash2, AlertTriangle, Download, Upload, Database, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ControlPanel() {
  const [adjustmentForm, setAdjustmentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    adjustment_type: 'add',
    fly_ash: 0,
    interlock_6: 0,
    interlock_8: 0,
    cement: 0,
    reason: '',
    notes: ''
  });
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [stock, setStock] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Backup & Restore states
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restorePassword, setRestorePassword] = useState('');
  const [restorePasswordError, setRestorePasswordError] = useState('');
  const [selectedBackupFile, setSelectedBackupFile] = useState(null);
  const [backupPreview, setBackupPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Check if user is admin
    const role = localStorage.getItem('userRole');
    setIsAdmin(role === 'admin');
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [historyRes, stockRes] = await Promise.all([
        axios.get(`${API}/stock/adjustments`, { headers }),
        axios.get(`${API}/stock`, { headers })
      ]);
      setAdjustmentHistory(historyRes.data);
      setStock(stockRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleAdjustment = async (e) => {
    e.preventDefault();
    
    if (adjustmentForm.fly_ash === 0 && adjustmentForm.interlock_6 === 0 && adjustmentForm.interlock_8 === 0 && adjustmentForm.cement === 0) {
      toast.error('Please enter at least one quantity to adjust');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/stock/adjust`, adjustmentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Stock ${adjustmentForm.adjustment_type === 'add' ? 'added' : 'reduced'} successfully`);
      setAdjustmentForm({
        date: new Date().toISOString().split('T')[0],
        adjustment_type: 'add',
        fly_ash: 0,
        interlock_6: 0,
        interlock_8: 0,
        cement: 0,
        reason: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust stock');
    }
  };

  const handleResetData = async () => {
    if (!resetPassword) {
      setPasswordError('Please enter the reset password');
      return;
    }
    
    setIsResetting(true);
    setPasswordError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/reset-data?reset_password=${encodeURIComponent(resetPassword)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All data has been reset successfully');
      setShowResetDialog(false);
      setResetPassword('');
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        setPasswordError('Invalid password. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to reset data');
      }
    } finally {
      setIsResetting(false);
    }
  };

  const handleBackup = async () => {
    setIsBackingUp(true);
    try {
      const token = localStorage.getItem('token');
      
      // First get the data
      const response = await axios.get(`${API}/backup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const backupData = JSON.stringify(response.data, null, 2);
      const date = new Date().toISOString().split('T')[0];
      const filename = `dhaya_bricks_backup_${date}.json`;
      
      // Try multiple download methods
      try {
        // Method 1: Blob URL (preferred)
        const blob = new Blob([backupData], { type: 'application/json' });
        const blobUrl = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        
        // For Safari on iOS
        if (typeof link.download === 'undefined') {
          link.target = '_blank';
        }
        
        document.body.appendChild(link);
        link.click();
        
        // Cleanup after delay
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);
        
      } catch (e) {
        // Method 2: Data URI fallback
        console.log('Using data URI fallback');
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(backupData);
        const downloadAnchor = document.createElement('a');
        downloadAnchor.setAttribute("href", dataStr);
        downloadAnchor.setAttribute("download", filename);
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        document.body.removeChild(downloadAnchor);
      }
      
      toast.success(`Backup "${filename}" downloaded! Check your Downloads folder.`);
    } catch (error) {
      console.error('Backup error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a valid JSON backup file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (!data.backup_date || !data.data) {
          toast.error('Invalid backup file format');
          return;
        }
        setSelectedBackupFile(data);
        setBackupPreview(data);
        setShowRestoreDialog(true);
      } catch (err) {
        toast.error('Failed to parse backup file');
      }
    };
    reader.readAsText(file);
  };

  const handleRestore = async () => {
    if (!selectedBackupFile) return;
    
    if (!restorePassword) {
      setRestorePasswordError('Please enter the restore password');
      return;
    }
    
    setIsRestoring(true);
    setRestorePasswordError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/restore?restore_password=${encodeURIComponent(restorePassword)}`,
        selectedBackupFile,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Data restored successfully!');
      setShowRestoreDialog(false);
      setSelectedBackupFile(null);
      setBackupPreview(null);
      setRestorePassword('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        setRestorePasswordError('Invalid password. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to restore data');
      }
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div data-testid="control-panel-page" className="space-y-8">
      <div>
        <h1 className="font-manrope font-bold text-4xl text-concrete-900 tracking-tight" data-testid="control-panel-title">
          <Settings2 className="inline-block mr-3 h-10 w-10 text-brick-600" />
          Control Panel
        </h1>
        <p className="font-inter text-concrete-600 mt-2">Manual stock adjustments and system controls</p>
      </div>

      <Tabs defaultValue="stock" className="w-full">
        <TabsList className="bg-white border border-concrete-200">
          <TabsTrigger data-testid="stock-adjustment-tab" value="stock" className="font-manrope font-semibold">
            Stock Adjustment
          </TabsTrigger>
          <TabsTrigger data-testid="adjustment-history-tab" value="history" className="font-manrope font-semibold">
            Adjustment History
          </TabsTrigger>
          <TabsTrigger data-testid="backup-restore-tab" value="backup" className="font-manrope font-semibold text-blue-600">
            Backup {isAdmin && '& Restore'}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger data-testid="reset-data-tab" value="reset" className="font-manrope font-semibold text-red-600">
              Reset Data
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="stock" className="space-y-6 mt-6">
          {stock && (
            <div className="bg-brick-50 border border-brick-200 rounded-md p-6">
              <h3 className="font-manrope font-semibold text-sm text-brick-800 mb-3">Current Stock Levels</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-md p-3 border border-brick-200">
                  <p className="font-inter text-xs text-concrete-600 mb-1">Fly Ash Bricks</p>
                  <p className="font-manrope font-bold text-2xl text-brick-600 tabular-nums">{stock.fly_ash.toLocaleString()}</p>
                </div>
                <div className="bg-white rounded-md p-3 border border-brick-200">
                  <p className="font-inter text-xs text-concrete-600 mb-1">6" Interlock</p>
                  <p className="font-manrope font-bold text-2xl text-brick-600 tabular-nums">{stock.interlock_6.toLocaleString()}</p>
                </div>
                <div className="bg-white rounded-md p-3 border border-brick-200">
                  <p className="font-inter text-xs text-concrete-600 mb-1">8" Interlock</p>
                  <p className="font-manrope font-bold text-2xl text-brick-600 tabular-nums">{stock.interlock_8.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white border border-concrete-200 shadow-sm rounded-md p-8">
            <h2 className="font-manrope font-bold text-xl text-concrete-900 mb-6">Manual Stock Adjustment</h2>
            <form onSubmit={handleAdjustment} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Date</Label>
                  <Input
                    type="date"
                    data-testid="adjustment-date"
                    value={adjustmentForm.date}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, date: e.target.value })}
                    className="mt-2 border-concrete-300 focus:border-brick-500"
                    required
                  />
                </div>

                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Adjustment Type</Label>
                  <Select value={adjustmentForm.adjustment_type} onValueChange={(value) => setAdjustmentForm({ ...adjustmentForm, adjustment_type: value })}>
                    <SelectTrigger data-testid="adjustment-type" className="mt-2 border-concrete-300 focus:border-brick-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="add">
                        <span className="flex items-center gap-2">
                          <Plus className="h-4 w-4 text-green-600" />
                          Add Stock
                        </span>
                      </SelectItem>
                      <SelectItem value="reduce">
                        <span className="flex items-center gap-2">
                          <Minus className="h-4 w-4 text-red-600" />
                          Reduce Stock
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Fly Ash Bricks</Label>
                  <Input
                    type="number"
                    min="0"
                    data-testid="adjustment-fly-ash"
                    value={adjustmentForm.fly_ash}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, fly_ash: parseInt(e.target.value) || 0 })}
                    className="mt-2 border-concrete-300 focus:border-brick-500 tabular-nums"
                  />
                </div>

                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">6" Interlock</Label>
                  <Input
                    type="number"
                    min="0"
                    data-testid="adjustment-interlock-6"
                    value={adjustmentForm.interlock_6}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, interlock_6: parseInt(e.target.value) || 0 })}
                    className="mt-2 border-concrete-300 focus:border-brick-500 tabular-nums"
                  />
                </div>

                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">8" Interlock</Label>
                  <Input
                    type="number"
                    min="0"
                    data-testid="adjustment-interlock-8"
                    value={adjustmentForm.interlock_8}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, interlock_8: parseInt(e.target.value) || 0 })}
                    className="mt-2 border-concrete-300 focus:border-brick-500 tabular-nums"
                  />
                </div>

                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Cement (kg)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    data-testid="adjustment-cement"
                    value={adjustmentForm.cement}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, cement: parseFloat(e.target.value) || 0 })}
                    className="mt-2 border-concrete-300 focus:border-brick-500 tabular-nums"
                  />
                </div>
              </div>

              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Reason</Label>
                <Input
                  data-testid="adjustment-reason"
                  value={adjustmentForm.reason}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                  className="mt-2 border-concrete-300 focus:border-brick-500"
                  placeholder="e.g., Damaged goods, Inventory correction, Return from customer"
                  required
                />
              </div>

              <div>
                <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Notes (Optional)</Label>
                <Textarea
                  data-testid="adjustment-notes"
                  value={adjustmentForm.notes}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, notes: e.target.value })}
                  className="mt-2 border-concrete-300 focus:border-brick-500"
                  rows={2}
                  placeholder="Additional details about this adjustment..."
                />
              </div>

              <Button
                type="submit"
                data-testid="submit-adjustment-button"
                className="bg-brick-600 hover:bg-brick-500 text-white shadow-md hover:shadow-lg transition-all duration-200 font-manrope font-semibold rounded-md px-6 py-2.5"
              >
                {adjustmentForm.adjustment_type === 'add' ? (
                  <><Plus className="mr-2 h-4 w-4" /> Add Stock</>
                ) : (
                  <><Minus className="mr-2 h-4 w-4" /> Reduce Stock</>
                )}
              </Button>
            </form>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <div data-testid="adjustment-history-list" className="bg-white border border-concrete-200 shadow-sm rounded-md overflow-hidden">
            <div className="p-6 border-b border-concrete-200">
              <h2 className="font-manrope font-bold text-xl text-concrete-900 flex items-center gap-2">
                <History className="h-5 w-5 text-brick-600" />
                Stock Adjustment History
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-concrete-50 border-b border-concrete-200">
                  <tr>
                    <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Date</th>
                    <th className="px-6 py-3 text-center font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Type</th>
                    <th className="px-6 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Fly Ash</th>
                    <th className="px-6 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">6" Interlock</th>
                    <th className="px-6 py-3 text-right font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">8" Interlock</th>
                    <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Reason</th>
                    <th className="px-6 py-3 text-left font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-concrete-100">
                  {adjustmentHistory.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center font-inter text-concrete-500">No adjustment history</td>
                    </tr>
                  ) : (
                    adjustmentHistory.map((adj) => (
                      <tr key={adj.id} className="hover:bg-concrete-50">
                        <td className="px-6 py-4 font-inter text-concrete-700">{adj.date}</td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                            adj.adjustment_type === 'add' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {adj.adjustment_type === 'add' ? <Plus className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                            {adj.adjustment_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-inter text-concrete-900 text-right tabular-nums">{adj.fly_ash}</td>
                        <td className="px-6 py-4 font-inter text-concrete-900 text-right tabular-nums">{adj.interlock_6}</td>
                        <td className="px-6 py-4 font-inter text-concrete-900 text-right tabular-nums">{adj.interlock_8}</td>
                        <td className="px-6 py-4 font-inter text-concrete-700">{adj.reason}</td>
                        <td className="px-6 py-4 font-inter text-concrete-600 text-sm">{adj.notes || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="backup" className="mt-6">
          <div className={`grid grid-cols-1 ${isAdmin ? 'lg:grid-cols-2' : ''} gap-6`}>
            {/* Backup Section */}
            <div className="bg-white border border-blue-200 shadow-sm rounded-md p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Download className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-manrope font-bold text-xl text-blue-900">Backup Data</h2>
                  <p className="font-inter text-blue-700 mt-1">Download all your data as a JSON file</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <h3 className="font-manrope font-semibold text-blue-800 mb-2">Backup includes:</h3>
                <ul className="list-disc list-inside font-inter text-blue-700 space-y-1">
                  <li>All workers and their earnings</li>
                  <li>All production records</li>
                  <li>All sales and customer data</li>
                  <li>All advance payments</li>
                  <li>Stock levels and adjustments</li>
                  <li>Settings and piece rates</li>
                </ul>
              </div>

              <Button
                onClick={handleBackup}
                disabled={isBackingUp}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-md font-manrope font-semibold"
              >
                <Download className="mr-2 h-4 w-4" />
                {isBackingUp ? 'Creating Backup...' : 'Download Backup'}
              </Button>
            </div>

            {/* Restore Section - Admin Only */}
            {isAdmin && (
            <div className="bg-white border border-green-200 shadow-sm rounded-md p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-green-100 rounded-full">
                  <Upload className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h2 className="font-manrope font-bold text-xl text-green-900">Restore Data</h2>
                  <p className="font-inter text-green-700 mt-1">Import data from a backup file</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
                <p className="font-inter text-amber-800">
                  <strong>Warning:</strong> Restoring will replace ALL existing data with the backup data. Make sure to create a backup first if needed.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">
                    Select Backup File (.json)
                  </Label>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    className="mt-2 cursor-pointer"
                  />
                </div>
              </div>
            </div>
            )}
          </div>
        </TabsContent>

        {isAdmin && (
        <TabsContent value="reset" className="mt-6">
          <div className="bg-white border border-red-200 shadow-sm rounded-md p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <h2 className="font-manrope font-bold text-xl text-red-900">Reset All Data</h2>
                <p className="font-inter text-red-700 mt-1">This action will permanently delete all transaction data</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <h3 className="font-manrope font-semibold text-red-800 mb-2">This will delete:</h3>
              <ul className="list-disc list-inside font-inter text-red-700 space-y-1">
                <li>All sales records</li>
                <li>All production entries</li>
                <li>All advance payments</li>
                <li>All stock adjustment history</li>
                <li>Reset all worker earnings and advances to ₹0</li>
                <li>Reset all stock levels to 0</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
              <p className="font-inter text-amber-800">
                <strong>Note:</strong> Worker profiles and settings (piece rates) will be preserved. Only transaction data will be deleted.
              </p>
            </div>

            <Button
              data-testid="reset-data-button"
              onClick={() => setShowResetDialog(true)}
              className="bg-red-600 hover:bg-red-700 text-white shadow-md font-manrope font-semibold"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Reset All Data
            </Button>
          </div>
        </TabsContent>
        )}
      </Tabs>

      {/* Reset Data Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={(open) => {
        setShowResetDialog(open);
        if (!open) {
          setResetPassword('');
          setPasswordError('');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-manrope font-bold text-red-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Confirm Data Reset
            </AlertDialogTitle>
            <AlertDialogDescription className="font-inter">
              Are you absolutely sure you want to reset all data? This action <strong>cannot be undone</strong>. 
              All sales, production, advances, and stock data will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">
              Enter Reset Password to Confirm
            </Label>
            <Input
              type="password"
              value={resetPassword}
              onChange={(e) => {
                setResetPassword(e.target.value);
                setPasswordError('');
              }}
              className={`mt-2 ${passwordError ? 'border-red-500' : ''}`}
              placeholder="Enter password"
              disabled={isResetting}
            />
            {passwordError && (
              <p className="text-red-600 text-sm mt-1">{passwordError}</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting} onClick={() => {
              setResetPassword('');
              setPasswordError('');
            }}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                handleResetData();
              }} 
              className="bg-red-600 hover:bg-red-700"
              disabled={isResetting || !resetPassword}
            >
              {isResetting ? 'Resetting...' : 'Yes, Reset All Data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Data Confirmation Dialog */}
      <AlertDialog open={showRestoreDialog} onOpenChange={(open) => {
        setShowRestoreDialog(open);
        if (!open) {
          setRestorePassword('');
          setRestorePasswordError('');
        }
      }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-manrope font-bold text-green-900 flex items-center gap-2">
              <Database className="h-5 w-5 text-green-600" />
              Confirm Data Restore
            </AlertDialogTitle>
            <AlertDialogDescription className="font-inter">
              You are about to restore data from a backup. This will <strong>replace all existing data</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {backupPreview && (
            <div className="bg-concrete-50 rounded-md p-4 my-4">
              <h4 className="font-manrope font-semibold text-sm mb-3">Backup Details:</h4>
              <div className="space-y-2 text-sm">
                <p className="flex justify-between">
                  <span className="text-concrete-600">Backup Date:</span>
                  <span className="font-semibold">{new Date(backupPreview.backup_date).toLocaleString()}</span>
                </p>
                {backupPreview.counts && (
                  <>
                    <p className="flex justify-between">
                      <span className="text-concrete-600">Workers:</span>
                      <span className="font-semibold">{backupPreview.counts.workers || 0}</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-concrete-600">Production Records:</span>
                      <span className="font-semibold">{backupPreview.counts.production || 0}</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-concrete-600">Sales:</span>
                      <span className="font-semibold">{backupPreview.counts.sales || 0}</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-concrete-600">Customers:</span>
                      <span className="font-semibold">{backupPreview.counts.customers || 0}</span>
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
          
          <div className="py-2">
            <Label className="font-manrope font-semibold uppercase tracking-wider text-xs text-concrete-700">
              Enter Password to Confirm Restore
            </Label>
            <Input
              type="password"
              value={restorePassword}
              onChange={(e) => {
                setRestorePassword(e.target.value);
                setRestorePasswordError('');
              }}
              className={`mt-2 ${restorePasswordError ? 'border-red-500' : ''}`}
              placeholder="Enter password"
              disabled={isRestoring}
            />
            {restorePasswordError && (
              <p className="text-red-600 text-sm mt-1">{restorePasswordError}</p>
            )}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRestoring} onClick={() => {
              setRestorePassword('');
              setRestorePasswordError('');
              setSelectedBackupFile(null);
              setBackupPreview(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                handleRestore();
              }} 
              className="bg-green-600 hover:bg-green-700"
              disabled={isRestoring || !restorePassword}
            >
              {isRestoring ? 'Restoring...' : 'Yes, Restore Data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
